import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  MapContainer,
  TileLayer,
  ZoomControl,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getProjectsSummary, getProjectDetails } from '../services/api';
import AddProjectButton from '../components/AddProjectButton';
import SearchBar from '../components/SearchBar';
import MappingPanel from '../components/MappingPanel';
import { ProjectMarkers } from '../components/ProjectMarkers';
import './MapPage.css';
import ErrorBoundary from '../components/ErrorBoundary';
import TileLayerControl from '../components/TileLayerControl';
import LocateControl from '../components/LocateControl';
import RouteControl from '../components/RouteControl';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useVisitTracker from '../hooks/useVisitTracker';
import debounce from 'lodash/debounce';

// Configuration de l'icône par défaut de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.1/dist/images/marker-shadow.png',
});

// Composant ToastContainer personnalisé
const CustomToastContainer = ({
  position = "top-right",
  autoClose = 3000,
  hideProgressBar = false,
  newestOnTop = true,
  closeOnClick = true,
  rtl = false,
  pauseOnFocusLoss = true,
  draggable = true,
  pauseOnHover = true,
  theme = "light"
}) => (
  <ToastContainer
    position={position}
    autoClose={autoClose}
    hideProgressBar={hideProgressBar}
    newestOnTop={newestOnTop}
    closeOnClick={closeOnClick}
    rtl={rtl}
    pauseOnFocusLoss={pauseOnFocusLoss}
    draggable={draggable}
    pauseOnHover={pauseOnHover}
    theme={theme}
  />
);

// Composant de chargement mémorisé
const Loading = React.memo(() => (
  <div className="loading-overlay">
    <div className="loading-content">
      <div>Chargement des projets...</div>
    </div>
  </div>
));

// Composant d'erreur mémorisé
const ErrorMessage = React.memo(({ message, onRetry }) => (
  <div className="error-overlay">
    <div className="error-content">
      <p>{message}</p>
      <button onClick={onRetry}>Réessayer</button>
    </div>
  </div>
));

// Composant pour gérer les changements de position de la carte
const ChangeView = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    // Ne mettre à jour la vue que si on n'est pas en train de faire une animation flyTo
    if (!map._isAnimating) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  
  return null;
};

const MapPage = () => {
  // Ajout du suivi des visites
  useVisitTracker('map-page');

  const queryClient = useQueryClient();
  const mapRef = useRef(null);
  
  // Fonction pour récupérer le paramètre de ville depuis l'URL
  const getCityFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const ville = urlParams.get('ville');
    // Nettoyer l'entrée utilisateur
    if (ville) {
      // Supprimer les caractères spéciaux et limiter la longueur
      return ville.replace(/[^a-zA-Z0-9\s-]/g, '').substring(0, 50);
    }
    return null;
  };

  // Fonction pour obtenir les coordonnées d'une ville
  const getCityCoordinates = async (cityName) => {
    try {
      // Vérifier que le nom de la ville n'est pas vide
      if (!cityName || cityName.trim() === '') {
        return null;
      }

      // Encoder le nom de la ville pour l'URL
      const encodedCityName = encodeURIComponent(cityName.trim());
      
      // Ajouter un délai pour respecter les limites de l'API Nominatim
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodedCityName}&limit=1`, {
        headers: {
          'User-Agent': 'Arkastra/1.0'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la requête à Nominatim');
      }
      
      const data = await response.json();
      if (data && data.length > 0) {
        // Vérifier que les coordonnées sont valides
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
          return [lat, lon];
        }
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération des coordonnées de la ville:', error);
      return null;
    }
  };

  // État pour le centre de la carte
  const [mapCenter, setMapCenter] = useState([50.8503, 4.3517]); // Centre de l'Europe (Bruxelles)
  const [mapZoom, setMapZoom] = useState(4); // Zoom level pour voir l'Europe entière avec plus de contexte
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    types: [],
    programs: [],
    hideNoDescription: false,
    yearRange: [1470, new Date().getFullYear()]
  });

  // Créer une version debounced de la mise à jour des filtres
  const debouncedSetFilters = useMemo(
    () => debounce((newFilters) => {
      setFilters(newFilters);
    }, 500), // Attendre 500ms après le dernier changement avant de mettre à jour
    []
  );

  // Effet pour initialiser le centre de la carte en fonction de l'URL
  useEffect(() => {
    const initializeMapCenter = async () => {
      const cityName = getCityFromUrl();
      if (cityName) {
        const coordinates = await getCityCoordinates(cityName);
        if (coordinates) {
          setMapCenter(coordinates);
          setMapZoom(13); // Augmentation du niveau de zoom pour une meilleure vue de la ville
        }
      }
    };
    initializeMapCenter();
  }, []);

  // Fonction mémorisée pour récupérer les projets
  const fetchProjects = useCallback(async () => {
    const queryFilters = {
      type: filters.types.length > 0 ? filters.types : undefined,
      programmes: filters.programs.length > 0 ? filters.programs : undefined,
      est_populaire: undefined,
      hideNoDescription: filters.hideNoDescription,
      yearRange: filters.yearRange
    };
    const data = await getProjectsSummary(queryFilters);
    return Array.from(new Set(data.map((proj) => proj.id)))
      .map((id) => data.find((proj) => proj.id === id));
  }, [filters]);

  const {
    data: projects = [],
    error: projectsError,
    isLoading,
    refetch: refetchProjects
  } = useQuery({
    queryKey: ['projects', filters],
    queryFn: fetchProjects
  });

  // Gérer les erreurs de récupération des projets
  useEffect(() => {
    if (projectsError) {
      setError('Une erreur est survenue lors de la récupération des projets.');
    } else {
      setError(null);
    }
  }, [projectsError]);

  // Requête pour les détails du projet sélectionné
  const {
    data: selectedProject,
    isLoading: isLoadingProjectDetails
  } = useQuery({
    queryKey: ['project', selectedProjectId],
    queryFn: () => selectedProjectId ? getProjectDetails(selectedProjectId) : null,
    enabled: !!selectedProjectId
  });

  // Fonction mémorisée pour gérer le clic sur un marqueur
  const handleMarkerClick = useCallback((projectId) => {
    if (projectId === selectedProjectId) return;
    
    const project = projects.find((proj) => proj.id === projectId);
    if (project) {
      const lat = parseFloat(project.latitude);
      const lon = parseFloat(project.longitude);
      
      // Mettre à jour le projet sélectionné d'abord
      setSelectedProjectId(projectId);
      
      // Mettre à jour l'état de la carte
      setMapCenter([lat, lon]);
      setMapZoom(16);
      
      // Utiliser flyTo pour une animation plus douce
      if (mapRef.current) {
        mapRef.current._isAnimating = true;
        
        // Attendre que l'animation soit terminée avant de réinitialiser le flag
        mapRef.current.once('flyend', () => {
          mapRef.current._isAnimating = false;
        });
        
        // Lancer l'animation
        mapRef.current.flyTo([lat, lon], 16, {
          duration: 1.5,
          easeLinearity: 0.25
        });
      }
    }
  }, [selectedProjectId, projects]);

  // Fonction mémorisée pour gérer la sélection d'un projet depuis la barre de recherche
  const handleProjectSelect = useCallback((project) => {
    console.log('handleProjectSelect called with:', project);
    if (!project || !project.latitude || !project.longitude) {
      console.log('Invalid project:', project);
      return;
    }
    
    const lat = parseFloat(project.latitude);
    const lon = parseFloat(project.longitude);
    console.log('Setting map center:', { lat, lon });
    
    // Mettre à jour le projet sélectionné d'abord
    setSelectedProjectId(project.id);
    
    // Mettre à jour l'état de la carte
    setMapCenter([lat, lon]);
    setMapZoom(16);
    
    // Utiliser flyTo pour une animation plus douce
    if (mapRef.current) {
      mapRef.current._isAnimating = true;
      
      // Attendre que l'animation soit terminée avant de réinitialiser le flag
      mapRef.current.once('flyend', () => {
        mapRef.current._isAnimating = false;
      });
      
      // Lancer l'animation
      mapRef.current.flyTo([lat, lon], 16, {
        duration: 1.5,
        easeLinearity: 0.25
      });
    }
  }, []);

  // Fonction mémorisée pour sélectionner une adresse
  const selectAddress = useCallback((address) => {
    console.log('selectAddress called with:', address);
    if (!address || !address.lat || !address.lon) {
      console.log('Invalid address:', address);
      return;
    }
    
    const lat = parseFloat(address.lat);
    const lon = parseFloat(address.lon);
    console.log('Setting map center:', { lat, lon });
    setMapCenter([lat, lon]);
    setMapZoom(13);
  }, []);

  // Gestionnaire des changements de sélection du panneau de mappage
  const handleMappingPanelChange = useCallback((selections) => {
    debouncedSetFilters(prev => ({
      ...prev,
      types: selections.types,
      programs: selections.programs,
      hideNoDescription: selections.hideNoDescription,
      yearRange: selections.yearRange
    }));
  }, [debouncedSetFilters]);

  // Nettoyer le debounce lors du démontage du composant
  useEffect(() => {
    return () => {
      debouncedSetFilters.cancel();
    };
  }, [debouncedSetFilters]);

  // Memoïsation du contenu de la carte
  const mapContent = useMemo(() => {
    const selectedProjectLocation = selectedProject 
      ? [parseFloat(selectedProject.latitude), parseFloat(selectedProject.longitude)]
      : null;

    return (
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        className="map-container"
        whenCreated={(mapInstance) => {
          console.log('Map created');
          mapRef.current = mapInstance;
        }}
        zoomControl={false}
      >
        <ChangeView center={mapCenter} zoom={mapZoom} />
        <TileLayer url="http://{s}.tile.osm.org/{z}/{x}/{y}.png" />
        <ZoomControl position="bottomright"/>
        <LocateControl/>
        <RouteControl projectLocation={selectedProjectLocation}/>
        {!isLoading && !error && (
          <ProjectMarkers
            projects={projects}
            handleMarkerClick={handleMarkerClick}
            selectedProject={selectedProject}
            handleMoreInfoClick={handleMarkerClick}
            isLoadingDetails={isLoadingProjectDetails}
          />
        )}
        <TileLayerControl/>
      </MapContainer>
    );
  }, [mapCenter, mapZoom, projects, handleMarkerClick, selectedProject, isLoading, error, isLoadingProjectDetails]);

  return (
    <ErrorBoundary>
      <div className="map-page-container">
        <div className="control-panel">
          <SearchBar 
            onSelectAddress={selectAddress} 
            onSelectProject={handleProjectSelect}
          />
          <AddProjectButton className="add-project-button" aria-label="Ajouter un projet" />
        </div>
        {mapContent}
        <MappingPanel onSelectionChange={handleMappingPanelChange} />
        {isLoading && <Loading />}
        {error && (
          <ErrorMessage 
            message={error} 
            onRetry={() => {
              queryClient.invalidateQueries(['projects']);
              refetchProjects();
            }} 
          />
        )}
        <CustomToastContainer />
      </div>
    </ErrorBoundary>
  );
};

export default React.memo(MapPage);